import { Injectable } from '@angular/core';
import * as fromRoot from '../../reducers';
import { Store, select } from '@ngrx/store';
import * as SiteActionTypes from './actions/site.actions';
import { SteamCost, EditSteamCost } from '../site.interfaces';
import {
	EditFitting,
	EditFolder,
	EditHearingProtectionType,
	EditInsulationType,
	EditIsoNotVerifiedReason,
	EditStatus,
	EditTeam,
	EditVendor,
	NewFitting,
	NewFolder,
	NewHearingProtectionType,
	NewInsulationType,
	NewIsoNotVerifiedReason,
	NewStatus,
	NewTeam,
	NewVendor,
} from '../site-settings.interfaces';

@Injectable({
	providedIn: 'root',
})
export class SiteStoreService {
	site$ = this.store.pipe(select(fromRoot.getSite));
	siteNames$ = this.store.pipe(select(fromRoot.getSiteNames));
	teams$ = this.store.pipe(select(fromRoot.getTeams));
	vendors$ = this.store.pipe(select(fromRoot.getVendors));
	folders$ = this.store.pipe(select(fromRoot.getFolders));
	hearingProtectionTypes$ = this.store.pipe(select(fromRoot.getHearingProtectionTypes));
	isolationNotVerifiedReasons$ = this.store.pipe(select(fromRoot.getIsolationNotVerifiedTypes));
	orificeSizes$ = this.store.pipe(select(fromRoot.getOrificeSizes));
	plumeLengths$ = this.store.pipe(select(fromRoot.getPlumeLengths));
	leakStatuses$ = this.store.pipe(select(fromRoot.getLeakStatuses));
	leakTypes$ = this.store.pipe(select(fromRoot.getLeakTypes));
	fittings$ = this.store.pipe(select(fromRoot.getFittings));
	fittingSizes$ = this.store.pipe(select(fromRoot.getFittingSizes));
	insulationTypes$ = this.store.pipe(select(fromRoot.getInsulationTypes));
	steamCosts$ = this.store.pipe(select(fromRoot.getSteamCosts));
	steamCostsLoading$ = this.store.pipe(select(fromRoot.getSteamCostsLoading));
	steamCostsSubmitting$ = this.store.pipe(select(fromRoot.getSteamCostsSubmitting));
	leakFormDetailsLoading$ = this.store.pipe(select(fromRoot.getLeakFormDetailsLoading));
	siteSettingsSaving$ = this.store.pipe(select(fromRoot.getSiteSettingsSaving));
	siteSettingsSaved$ = this.store.pipe(select(fromRoot.getSiteSettingsSaved));
	siteError$ = this.store.pipe(select(fromRoot.getSiteError));
	unitPreferences$ = this.store.pipe(select(fromRoot.getUnitPreferences));
	constructor(private store: Store<fromRoot.State>) {}

	dispatchGetSite(siteId: string) {
		this.store.dispatch(new SiteActionTypes.GetSite(siteId));
	}

	dispatchGetSiteNames() {
		this.store.dispatch(new SiteActionTypes.GetSiteNames());
	}

	dispatchGetLeakFormDetailsForSite(siteId: string) {
		this.store.dispatch(new SiteActionTypes.GetLeakFormDetailsForSite(siteId));
	}

	dispatchGetSteamCosts(siteId: string) {
		this.store.dispatch(new SiteActionTypes.GetSteamCosts(siteId));
	}

	dispatchUpdateSteamCosts(steamCosts: EditSteamCost[]) {
		this.store.dispatch(new SiteActionTypes.UpdateSteamCosts(steamCosts));
	}

	dispatchAddFitting(newFitting: NewFitting) {
		this.store.dispatch(new SiteActionTypes.AddFitting(newFitting));
	}

	dispatchUpdateFitting(editFitting: EditFitting) {
		this.store.dispatch(new SiteActionTypes.UpdateFitting(editFitting));
	}

	dispatchDeleteFitting(fittingId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteFitting(fittingId));
	}

	dispatchAddFolder(newFolder: NewFolder) {
		this.store.dispatch(new SiteActionTypes.AddFolder(newFolder));
	}

	dispatchUpdateFolder(editFolder: EditFolder) {
		this.store.dispatch(new SiteActionTypes.UpdateFolder(editFolder));
	}

	dispatchDeleteFolder(folderId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteFolder(folderId));
	}

	dispatchAddHearingProtectionType(newHearingProtectionType: NewHearingProtectionType) {
		this.store.dispatch(new SiteActionTypes.AddHearingProtectionType(newHearingProtectionType));
	}

	dispatchUpdateHearingProtectionType(editHearingProtectionType: EditHearingProtectionType) {
		this.store.dispatch(new SiteActionTypes.UpdateHearingProtectionType(editHearingProtectionType));
	}

	dispatchDeleteHearingProtectionType(hearingProtectionTypeId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteHearingProtectionType(hearingProtectionTypeId));
	}

	dispatchAddInsulationType(newInsulationType: NewInsulationType) {
		this.store.dispatch(new SiteActionTypes.AddInsulationType(newInsulationType));
	}

	dispatchUpdateInsulationType(editInsulationType: EditInsulationType) {
		this.store.dispatch(new SiteActionTypes.UpdateInsulationType(editInsulationType));
	}

	dispatchDeleteInsulationType(insulationTypeId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteInsulationType(insulationTypeId));
	}

	dispatchAddIsoNotVerifiedReason(newIsoNotVerifiedReason: NewIsoNotVerifiedReason) {
		this.store.dispatch(new SiteActionTypes.AddIsoNotVerifiedReason(newIsoNotVerifiedReason));
	}

	dispatchUpdateIsoNotVerifiedReason(editIsoNotVerifiedReason: EditIsoNotVerifiedReason) {
		this.store.dispatch(new SiteActionTypes.UpdateIsoNotVerifiedReason(editIsoNotVerifiedReason));
	}

	dispatchDeleteIsoNotVerifiedReason(isoNotVerifiedReasonId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteIsoNotVerifiedReason(isoNotVerifiedReasonId));
	}

	dispatchAddStatus(newStatus: NewStatus) {
		this.store.dispatch(new SiteActionTypes.AddStatus(newStatus));
	}

	dispatchUpdateStatus(editStatus: EditStatus) {
		this.store.dispatch(new SiteActionTypes.UpdateStatus(editStatus));
	}

	dispatchDeleteStatus(statusId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteStatus(statusId));
	}

	dispatchAddTeam(newTeam: NewTeam) {
		this.store.dispatch(new SiteActionTypes.AddTeam(newTeam));
	}

	dispatchUpdateTeam(editTeam: EditTeam) {
		this.store.dispatch(new SiteActionTypes.UpdateTeam(editTeam));
	}

	dispatchDeleteTeam(teamId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteTeam(teamId));
	}

	dispatchAddVendor(newVendor: NewVendor) {
		this.store.dispatch(new SiteActionTypes.AddVendor(newVendor));
	}

	dispatchUpdateVendor(editVendor: EditVendor) {
		this.store.dispatch(new SiteActionTypes.UpdateVendor(editVendor));
	}

	dispatchDeleteVendor(vendorId: string) {
		this.store.dispatch(new SiteActionTypes.DeleteVendor(vendorId));
	}

	dispatchClearSite() {
		this.store.dispatch(new SiteActionTypes.ClearSite());
	}
}
